import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import sanitizeHtml from 'sanitize-html';

const actions = require('actions/librarian_chat');
const LibrarianReader = require('components/shared/librarian_reader');
const LibrarianReaderEmbed = require('components/shared/librarian_reader_embed');
const Modal = require('components/shared/modal');

const styles = require('./librarian_experience.styl');
const cx = classNames.bind(styles);
const LIBRARIAN_MEDIA_BASE_PATH = typeof LIBRARIAN_BASE_PATH !== 'undefined'
  ? LIBRARIAN_BASE_PATH
  : '/librarian';

const loadHlsLibrary = () => new Promise((resolve, reject) => {
  require.ensure([], (require) => {
    const hlsModule = require('hls.js');
    resolve(hlsModule.default || hlsModule);
  }, reject, 'librarian-video-player');
});

const copy = {
  empty: "Start the conversation by telling the librarian what you'd like to learn about.",
  untitled: 'Untitled Article',
  readIt: 'Read It!',
  inputPlaceholderInitial: "Ask a question or tell me what you want to read about!",
  inputPlaceholderFollowUp: "Not what you're looking for? Ask for something more specific...",
  reset: 'Start Over',
  send: 'Send',
  thinking: 'Hmmm. Let me see...',
  streaming: '...',
  moreDetails: 'More details',
  keepExploring: 'Keep exploring',
  goDeeper: 'Go Deeper',
  watchIt: 'Watch It!',
  videoLoading: 'Loading video...',
  videoError: 'Unable to play this video.',
};

const generateReadingSessionId = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  const randomPart = Math.random().toString(36).slice(2, 12);
  return `${Date.now()}-${randomPart}`;
};

const sanitizeOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat(['ul', 'ol', 'li', 'img', 'h1','h2','h3','h4','h5','h6']),
  allowedAttributes: Object.assign({}, sanitizeHtml.defaults.allowedAttributes, {
    img: ['src', 'alt', 'title', 'loading'],
    a: ['href', 'title', 'target', 'rel'],
  }),
};

const renderMarkdown = (text) => {
  const source = text || '';
  let rawHtml = source;
  if (typeof window !== 'undefined' && window.marked) {
    try {
      const markedLib = window.marked;
      if (typeof markedLib.parse === 'function') {
        const parsed = markedLib.parse(source);
        rawHtml = typeof parsed === 'string' ? parsed : source;
      } else if (typeof markedLib === 'function') {
        const parsed = markedLib(source);
        rawHtml = typeof parsed === 'string' ? parsed : source;
      }
    } catch (error) {
      rawHtml = source;
    }
  }
  const clean = sanitizeHtml(rawHtml, sanitizeOptions);
  return { __html: clean };
};

const stringValue = (value) => `${value || ''}`.trim();
const firstString = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const cleaned = stringValue(values[index]);
    if (cleaned) {
      return cleaned;
    }
  }
  return '';
};

const normalizeContinuations = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const normalizeItem = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return null;
    }
    const question = stringValue(item.question || item.text || item.message);
    return question ? Object.assign({}, item, { question }) : null;
  };
  return {
    adjacent: normalizeItem(source.adjacent),
    deeper: normalizeItem(source.deeper),
  };
};

const getLegacyContent = (message) => {
  if (Array.isArray(message.recommendations) && message.recommendations.length) {
    return {
      type: 'recommendations',
      header: message.meta && message.meta.header ? message.meta.header : message.text || '',
      footer: message.meta && message.meta.footer ? message.meta.footer : '',
      items: message.recommendations,
    };
  }
  return { type: 'message', text: message.text || message.message || '' };
};

const getMessageContent = (message = {}) => {
  const content = message.content && typeof message.content === 'object' && !Array.isArray(message.content)
    ? message.content
    : getLegacyContent(message);
  const type = `${content.type || 'message'}`.trim().toLowerCase();
  if (type === 'qa' || type === 'q&a') {
    return {
      type: 'qa',
      answer: stringValue(content.answer || content.text),
      key_points: Array.isArray(content.key_points) ? content.key_points.map(stringValue).filter(Boolean) : [],
      deep_answer: stringValue(content.deep_answer || content.deepAnswer),
      sources: Array.isArray(content.sources) ? content.sources : [],
      continuations: normalizeContinuations(content.continuations),
    };
  }
  if (type === 'recommendations' || type === 'recommendation') {
    return {
      type: 'recommendations',
      header: stringValue(content.header || content.text || content.message),
      footer: stringValue(content.footer),
      items: Array.isArray(content.items)
        ? content.items
        : Array.isArray(message.recommendations)
          ? message.recommendations
          : [],
    };
  }
  return {
    type: 'message',
    text: stringValue(content.text || content.message || message.text || message.message),
    reason: content.reason,
  };
};

const splitParagraphs = (text) =>
  stringValue(text)
    .replace(/\\n/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const getRecommendationImage = (recommendation = {}) =>
  firstString(
    recommendation.thumbnail_url,
    recommendation.poster_url,
    recommendation.image_url,
    recommendation.cover_url,
  );

const getVideoUrl = (recommendation = {}) =>
  firstString(
    recommendation.stream_url,
    recommendation.video_url,
    recommendation.media_url,
    recommendation.mp4_url,
    recommendation.hls_url,
    recommendation.dash_url,
  );

const isVideoRecommendation = (recommendation = {}) => {
  const type = firstString(
    recommendation.type,
    recommendation.format,
    recommendation.media_type,
    recommendation.content_type,
    recommendation.asset_type,
  ).toLowerCase();
  return type.indexOf('video') >= 0 || Boolean(getVideoUrl(recommendation));
};

const getRecommendationDescription = (recommendation = {}) =>
  firstString(
    recommendation.llm_description,
    recommendation.why,
    recommendation.description,
    recommendation.teaser,
    recommendation.excerpt,
  );

const formatDurationSeconds = (durationSeconds) => {
  const totalSeconds = Math.round(Number(durationSeconds));
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '';
  }
  const padTime = (value) => (value < 10 ? `0${value}` : `${value}`);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${padTime(minutes)}:${padTime(seconds)}`;
  }
  return `${minutes}:${padTime(seconds)}`;
};

const getRecommendationDuration = (recommendation = {}) =>
  firstString(
    recommendation.duration_label,
    recommendation.duration,
    recommendation.runtime,
    recommendation.running_time,
    formatDurationSeconds(recommendation.duration_seconds),
  );

const LibrarianVideoPlayer = ({ assetId, fallbackSrc, fullScreen, poster, title }) => {
  const videoRef = useRef(null);
  const [isLoading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    let cancelled = false;
    let hlsInstance = null;
    let usingFallback = false;
    let networkRecoveryAttempts = 0;
    let mediaRecoveryAttempts = 0;

    setLoading(true);
    setError(false);

    const loadFallback = () => {
      if (cancelled || usingFallback) {
        return;
      }
      usingFallback = true;
      if (!fallbackSrc) {
        setLoading(false);
        setError(true);
        return;
      }
      video.src = fallbackSrc;
      video.load();
    };

    const loadHls = async () => {
      if (!assetId) {
        loadFallback();
        return;
      }
      try {
        const response = await fetch(
          `${LIBRARIAN_MEDIA_BASE_PATH}/video/${encodeURIComponent(assetId)}/hls?resolve=true`,
          { headers: { Accept: 'application/json' } },
        );
        if (!response.ok) {
          throw new Error(`HLS source returned ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled || !payload.url) {
          return;
        }

        const Hls = await loadHlsLibrary();
        if (cancelled) {
          return;
        }
        if (!Hls.isSupported()) {
          if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = payload.url;
            video.load();
          } else {
            loadFallback();
          }
          return;
        }

        hlsInstance = new Hls();
        hlsInstance.on(Hls.Events.ERROR, (event, data) => {
          if (!data.fatal || cancelled) {
            return;
          }
          console.error('[AI Librarian video] Fatal HLS error', {
            type: data.type,
            details: data.details,
            reason: data.reason,
            responseCode: data.response && data.response.code,
          });
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && networkRecoveryAttempts < 1) {
            networkRecoveryAttempts += 1;
            hlsInstance.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveryAttempts < 2) {
            mediaRecoveryAttempts += 1;
            if (mediaRecoveryAttempts === 2 && hlsInstance.swapAudioCodec) {
              hlsInstance.swapAudioCodec();
            }
            hlsInstance.recoverMediaError();
            return;
          }
          hlsInstance.destroy();
          hlsInstance = null;
          loadFallback();
        });
        hlsInstance.on(Hls.Events.FRAG_BUFFERED, () => {
          networkRecoveryAttempts = 0;
        });
        hlsInstance.loadSource(payload.url);
        hlsInstance.attachMedia(video);
      } catch (error) {
        loadFallback();
      }
    };

    loadHls();

    return () => {
      cancelled = true;
      if (hlsInstance) {
        hlsInstance.destroy();
      }
      video.pause();
      video.removeAttribute('src');
    };
  }, [assetId, fallbackSrc]);

  const handleReady = () => {
    setLoading(false);
    setError(false);
  };

  const handleWaiting = () => {
    setLoading(true);
  };

  const handleError = () => {
    const video = videoRef.current;
    const mediaError = video && video.error;
    const currentSource = video && video.currentSrc
      ? video.currentSrc.split('?')[0]
      : '';
    console.error('[AI Librarian video] Media element error', {
      code: mediaError && mediaError.code,
      message: mediaError && mediaError.message,
      networkState: video && video.networkState,
      readyState: video && video.readyState,
      source: currentSource,
    });
    setLoading(false);
    setError(true);
  };

  return (
    <div
      className={cx('librarian-video-player', {
        'librarian-video-player--full-screen': fullScreen,
      })}
      role="document"
      aria-label={title || 'Recommended video'}
    >
      <video
        ref={videoRef}
        className={cx('librarian-video-player__video')}
        poster={poster || undefined}
        controls
        controlsList="nodownload"
        disablePictureInPicture
        playsInline
        preload="metadata"
        aria-label={title ? `Video: ${title}` : 'Recommended video'}
        onLoadStart={() => setLoading(true)}
        onLoadedMetadata={handleReady}
        onCanPlay={handleReady}
        onPlaying={handleReady}
        onWaiting={handleWaiting}
        onStalled={handleWaiting}
        onError={handleError}
      >
        Your browser does not support HTML video.
      </video>
      {isLoading && !hasError ? (
        <div className={cx('librarian-video-player__status')} aria-live="polite">
          <span className={cx('librarian-video-player__spinner')} />
          <span>{copy.videoLoading}</span>
        </div>
      ) : null}
      {hasError ? (
        <div className={cx('librarian-video-player__status', 'librarian-video-player__status--error')} role="alert">
          <span>{copy.videoError}</span>
        </div>
      ) : null}
    </div>
  );
};

LibrarianVideoPlayer.propTypes = {
  assetId: PropTypes.string,
  fallbackSrc: PropTypes.string,
  fullScreen: PropTypes.bool,
  poster: PropTypes.string,
  title: PropTypes.string,
};

LibrarianVideoPlayer.defaultProps = {
  assetId: '',
  fallbackSrc: '',
  fullScreen: false,
  poster: '',
  title: '',
};

const LibrarianVideoModal = ({ assetId, fallbackSrc, fullScreen, onClose, poster, title }) => (
  <Modal fullSize={fullScreen} eReader={fullScreen} onClose={onClose}>
    <LibrarianVideoPlayer
      assetId={assetId}
      fallbackSrc={fallbackSrc}
      fullScreen={fullScreen}
      poster={poster}
      title={title}
    />
  </Modal>
);

LibrarianVideoModal.propTypes = {
  assetId: PropTypes.string,
  fallbackSrc: PropTypes.string,
  fullScreen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  poster: PropTypes.string,
  title: PropTypes.string,
};

LibrarianVideoModal.defaultProps = {
  assetId: '',
  fallbackSrc: '',
  fullScreen: false,
  poster: '',
  title: '',
};

const MessageList = ({ messages, onOpenReader, onOpenLexileModal, isLexileModalOpen, onSubmitPrompt }) => {
  const threadRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  if (!messages.length) {
    return <div className={cx('thread-empty')}>{copy.empty}</div>;
  }

  return (
    <div className={cx('thread')} ref={threadRef}>
      {messages.map((message, index) => (
        <MessageBubble
          key={`${message.role}-${index}`}
          message={message}
          onOpenReader={onOpenReader}
          onOpenLexileModal={onOpenLexileModal}
          isLexileModalOpen={isLexileModalOpen}
          onSubmitPrompt={onSubmitPrompt}
        />
      ))}
    </div>
  );
};

MessageList.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
  onSubmitPrompt: PropTypes.func.isRequired,
};

const MessageBubble = ({ message, onOpenReader, onOpenLexileModal, isLexileModalOpen, onSubmitPrompt }) => {
  const isThinking = message.meta && message.meta.streaming;
  const content = getMessageContent(message);
  const displayText = content.type === 'qa'
    ? content.answer
    : content.type === 'recommendations'
      ? content.header
      : content.text;
  const hasStreamingContent = stringValue(displayText).length > 0
    || (content.type === 'recommendations' && content.items.length > 0)
    || (content.type === 'qa' && (content.key_points.length > 0 || content.deep_answer || content.sources.length > 0));
  const showThinkingState = isThinking && !hasStreamingContent;
  const librarianProfile = require('./assets/librarian-profile.png').default || require('./assets/librarian-profile.png');

  return (
    <div className={cx('message', `message--${message.role}`)}>
      {message.role === 'assistant' ? (
        <div className={cx('bubble')}>
          <div className={cx('profile')}>
            <img src={librarianProfile} alt="Librarian" />
          </div>
          <div className={cx('content')}>
            {showThinkingState ? (
              <div className={cx('text', { 'is-thinking': true })} dangerouslySetInnerHTML={renderMarkdown(copy.thinking)} />
            ) : (
              <AssistantContent
                content={content}
                onOpenReader={onOpenReader}
                onOpenLexileModal={onOpenLexileModal}
                isLexileModalOpen={isLexileModalOpen}
                onSubmitPrompt={onSubmitPrompt}
              />
            )}
            {isThinking && hasStreamingContent ? <StreamingIndicator /> : null}
          </div>
        </div>
      ) : (
        <div className={cx('bubble')}>
          <div
            className={cx('text', { 'is-thinking': showThinkingState })}
            dangerouslySetInnerHTML={renderMarkdown(showThinkingState ? copy.thinking : content.text || '')}
          />
        </div>
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    text: PropTypes.string,
    recommendations: PropTypes.array,
    content: PropTypes.object,
    meta: PropTypes.object,
  }).isRequired,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
  onSubmitPrompt: PropTypes.func.isRequired,
};

const StreamingIndicator = () => (
  <div className={cx('streaming-indicator')} aria-live="polite">
    <span className={cx('streaming-indicator__spinner')} />
    <span>{copy.streaming}</span>
  </div>
);

const AssistantContent = ({ content, onOpenReader, onOpenLexileModal, isLexileModalOpen, onSubmitPrompt }) => {
  if (content.type === 'qa') {
    return (
      <QaResponse
        content={content}
        onOpenReader={onOpenReader}
        onOpenLexileModal={onOpenLexileModal}
        isLexileModalOpen={isLexileModalOpen}
        onSubmitPrompt={onSubmitPrompt}
      />
    );
  }
  if (content.type === 'recommendations') {
    return (
      <RecommendationResponse
        content={content}
        onOpenReader={onOpenReader}
        onOpenLexileModal={onOpenLexileModal}
        isLexileModalOpen={isLexileModalOpen}
      />
    );
  }
  return <div className={cx('text')} dangerouslySetInnerHTML={renderMarkdown(content.text || '')} />;
};

AssistantContent.propTypes = {
  content: PropTypes.object.isRequired,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
  onSubmitPrompt: PropTypes.func.isRequired,
};

const RecommendationResponse = ({ content, onOpenReader, onOpenLexileModal, isLexileModalOpen }) => (
  <div className={cx('recommendation-response')}>
    {content.header ? <div className={cx('text')} dangerouslySetInnerHTML={renderMarkdown(content.header)} /> : null}
    <RecommendationSection
      recommendations={content.items || []}
      onOpenReader={onOpenReader}
      onOpenLexileModal={onOpenLexileModal}
      isLexileModalOpen={isLexileModalOpen}
    />
    {content.footer ? <div className={cx('text', 'text--footer')} dangerouslySetInnerHTML={renderMarkdown(content.footer)} /> : null}
  </div>
);

RecommendationResponse.propTypes = {
  content: PropTypes.object.isRequired,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
};

const QaResponse = ({ content, onOpenReader, onOpenLexileModal, isLexileModalOpen, onSubmitPrompt }) => {
  const continuations = content.continuations || {};
  const followUps = [
    { label: 'Nearby', item: continuations.adjacent },
    { label: 'Deeper', item: continuations.deeper },
  ].filter(({ item }) => item && item.question);

  return (
    <div className={cx('qa-response')}>
      {content.answer ? (
        <section className={cx('qa-answer')}>
          <div className={cx('text')} dangerouslySetInnerHTML={renderMarkdown(content.answer)} />
        </section>
      ) : null}
      {content.key_points && content.key_points.length ? (
        <ul className={cx('qa-key-points')}>
          {content.key_points.map((point, index) => (
            <li key={`${point}-${index}`}>
              <div className={cx('qa-key-point')} dangerouslySetInnerHTML={renderMarkdown(point)} />
            </li>
          ))}
        </ul>
      ) : null}
      {content.deep_answer ? (
        <details className={cx('qa-more-details')}>
          <summary>{copy.moreDetails}</summary>
          <div className={cx('qa-more-details__body')}>
            {splitParagraphs(content.deep_answer).map((paragraph, index) => (
              <div
                key={`${paragraph}-${index}`}
                className={cx('text', 'qa-detail-paragraph')}
                dangerouslySetInnerHTML={renderMarkdown(paragraph)}
              />
            ))}
          </div>
        </details>
      ) : null}
      <RecommendationSection
        title={copy.goDeeper}
        recommendations={content.sources || []}
        onOpenReader={onOpenReader}
        onOpenLexileModal={onOpenLexileModal}
        isLexileModalOpen={isLexileModalOpen}
      />
      {followUps.length ? (
        <section className={cx('qa-continuations')}>
          <h3>{copy.keepExploring}</h3>
          <div className={cx('qa-continuations__buttons')}>
            {followUps.map(({ label, item }) => (
              <button
                key={`${label}-${item.question}`}
                type="button"
                className={cx('qa-continuation')}
                onClick={() => onSubmitPrompt(item.question)}
              >
                <span className={cx('qa-continuation__label')}>{label}</span>
                <span className={cx('qa-continuation__question')}>{item.question}</span>
                {item.topic ? <span className={cx('qa-continuation__topic')}>{item.topic}</span> : null}
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

QaResponse.propTypes = {
  content: PropTypes.object.isRequired,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
  onSubmitPrompt: PropTypes.func.isRequired,
};

const RecommendationSection = ({ title, recommendations, onOpenReader, onOpenLexileModal, isLexileModalOpen }) => {
  const hasRecommendations = Array.isArray(recommendations) && recommendations.length > 0;
  if (!hasRecommendations) {
    return null;
  }
  return (
    <div className={cx('recommendations-group')}>
      {title ? <h3 className={cx('section-title')}>{title}</h3> : null}
      <div className={cx('recommendations')}>
        {recommendations.map((recommendation, index) => (
          <RecommendationCard
            key={recommendation.asset_id || `${recommendation.title || 'recommendation'}-${index}`}
            recommendation={recommendation}
            onOpenReader={onOpenReader}
          />
        ))}
      </div>
      <LexileLegend onHelp={onOpenLexileModal} isOpen={isLexileModalOpen} />
    </div>
  );
};

RecommendationSection.propTypes = {
  title: PropTypes.string,
  recommendations: PropTypes.array,
  onOpenReader: PropTypes.func.isRequired,
  onOpenLexileModal: PropTypes.func.isRequired,
  isLexileModalOpen: PropTypes.bool.isRequired,
};

RecommendationSection.defaultProps = {
  title: '',
  recommendations: [],
};

const RecommendationCard = ({ recommendation, onOpenReader }) => {
  const imageSrc = getRecommendationImage(recommendation);
  const isVideo = isVideoRecommendation(recommendation);
  const description = getRecommendationDescription(recommendation);
  const duration = getRecommendationDuration(recommendation);
  const formatLabel = isVideo
    ? 'Video'
    : firstString(recommendation.type, recommendation.format, recommendation.media_type, 'Article');

  return (
    <article className={cx('card', { 'card--video': isVideo })}>
      {imageSrc ? (
        <LexileThumb
          src={imageSrc}
          title={recommendation.title}
          lexile={recommendation.lexile}
          isVideo={isVideo}
        />
      ) : null}
      <div className={cx('card-body')}>
        <div className={cx('card-meta')}>
          {formatLabel ? <span className={cx('card-type')}>{formatLabel}</span> : null}
          {duration ? <span>{duration}</span> : null}
        </div>
        <h3>{recommendation.title || copy.untitled}</h3>
        {description ? <p>{description}</p> : null}
        <div className={cx('actions')}>
          <button
            type="button"
            className={cx('button', 'button--primary')}
            onClick={() => onOpenReader(recommendation)}
          >
            {isVideo ? copy.watchIt : copy.readIt}
          </button>
        </div>
      </div>
    </article>
  );
};

RecommendationCard.propTypes = {
  recommendation: PropTypes.shape({
    title: PropTypes.string,
    teaser: PropTypes.string,
    description: PropTypes.string,
    excerpt: PropTypes.string,
    llm_description: PropTypes.string,
    why: PropTypes.string,
    lexile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    thumbnail_url: PropTypes.string,
    poster_url: PropTypes.string,
    image_url: PropTypes.string,
    cover_url: PropTypes.string,
    asset_id: PropTypes.string,
    download_url: PropTypes.string,
    stream_url: PropTypes.string,
    video_url: PropTypes.string,
    media_url: PropTypes.string,
    mp4_url: PropTypes.string,
    hls_url: PropTypes.string,
    dash_url: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    media_type: PropTypes.string,
    content_type: PropTypes.string,
    asset_type: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration_label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration_seconds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    runtime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    running_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onOpenReader: PropTypes.func.isRequired,
};

const LEXILE_BANDS = [
  { badge: 0, min: 0, max: 190, color: '#C31F33', desc: 'simple sentences with images and repeated words' },
  { badge: 1, min: 191, max: 530, color: '#FBAB18',  desc: 'simple paragraphs with short sentence'},
  { badge: 2, min: 531, max: 650, color: '#FFD227', desc: 'multiple paragraphs with sentences of varying lengths' },
  { badge: 3, min: 651, max: 820, color: '#7AC142', desc: 'multiple complex paragraphs with larger vocabulary limited visual support' },
  { badge: 4, min: 821, max: 940, color: '#00BAF2', desc: 'multiple complex paragraphs with limited visual and vocabulary support' },
  { badge: 5, min: 941, max: 1070, color: '#0069AA', desc: 'multiple complex paragraphs with topic-specific vocabulary and limited to no visual support' },
  { badge: 6, min: 1071, max: 1551, color: '#9C258F', desc: 'longer and more complex, including technical, academic vocabulary as well as colloquial expressions' }
];

const resolveLexileBadge = (lexileValue) => {
  const cleaned = lexileValue === undefined || lexileValue === null ? '' : `${lexileValue}`.trim();
  const numeric = cleaned ? parseInt(cleaned, 10) : NaN;
  if (Number.isNaN(numeric)) {
    return { badge: null, color: '#C1C7D0' };
  }
  const band = LEXILE_BANDS.find((entry) => numeric >= entry.min && numeric <= entry.max);
  if (!band) {
    return { badge: null, color: '#C1C7D0' };
  }
  return { badge: band.badge, color: band.color };
};

const LexileThumb = ({ src, title, lexile, isVideo }) => {
  const { badge, color } = resolveLexileBadge(lexile);
  const borderStyle = { borderColor: color };

  return (
    <div
      className={cx('thumb', {
        'thumb--no-lexile': badge === null,
        'thumb--video': isVideo,
      })}
      style={borderStyle}
    >
      <img src={src} alt={`Cover for ${title || copy.untitled}`} loading="lazy" />
      {badge !== null ? <span className={cx('lexile-badge')} style={{ backgroundColor: color }}>{badge}</span> : null}
    </div>
  );
};

LexileThumb.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  lexile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isVideo: PropTypes.bool,
};

LexileThumb.defaultProps = {
  title: '',
  lexile: '',
  isVideo: false,
};

const LexileLegend = ({ onHelp, isOpen }) => (
  <div className={cx('lexile-legend')} role="group" aria-label="Lexile band legend">
    <span className={cx('lexile-legend__label', 'lexile-legend__label--easier')}>easier</span>
    <div className={cx('lexile-legend__bands')}>
      {LEXILE_BANDS.map((band) => (
        <span key={band.badge} className={cx('lexile-legend__band')} style={{ backgroundColor: band.color }}>
          {band.badge}
        </span>
      ))}
    </div>
    <span className={cx('lexile-legend__label', 'lexile-legend__label--harder')}>harder</span>
    <span className={cx('lexile-legend__spacer')} aria-hidden="true" />
    <button
      type="button"
      className={cx('lexile-legend__help')}
      aria-label="What are Lexile bands?"
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      onClick={onHelp}
    >
      ?
    </button>
  </div>
);

LexileLegend.propTypes = {
  onHelp: PropTypes.func,
  isOpen: PropTypes.bool,
};

LexileLegend.defaultProps = {
  onHelp: null,
  isOpen: false,
};

const formatLexileRange = (band) => {
  if (band.range) {
    return band.range;
  }
  if (band.min !== undefined && band.max !== undefined) {
    return `${band.min}L–${band.max}L`;
  }
  return '';
};

const getLexileBadgeValue = (badge) => {
  if (typeof badge === 'number') {
    return badge;
  }
  const parsed = parseInt(badge, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const LexileModalContent = () => {
  const orderedBands = [...LEXILE_BANDS].sort((a, b) => getLexileBadgeValue(a.badge) - getLexileBadgeValue(b.badge));
  const splitIndex = Math.ceil(orderedBands.length / 2);
  const leftBands = orderedBands.slice(0, splitIndex);
  const rightBands = orderedBands.slice(splitIndex);

  const renderBand = (band) => {
    const range = formatLexileRange(band);

    return (
    <div key={band.badge} className={cx('lexile-modal__item')}>
      <div className={cx('lexile-modal__header')}>
        <span className={cx('lexile-modal__level-label')} style={{ color: band.color }}>
          Level
        </span>
        <span className={cx('lexile-modal__level-badge')} style={{ backgroundColor: band.color }}>
          {band.badge}
        </span>
        {range ? <span className={cx('lexile-modal__range')}>{range}</span> : null}
      </div>
      {band.desc ? (
        <p className={cx('lexile-modal__description')}>{band.desc}</p>
      ) : null}
    </div>
    );
  };

  return (
    <div className={cx('lexile-modal')} role="document">
      <h2 className={cx('lexile-modal__title')}>Level Descriptions</h2>
      <div className={cx('lexile-modal__grid')}>
        <div className={cx('lexile-modal__column')}>
          {leftBands.map(renderBand)}
        </div>
        <div className={cx('lexile-modal__column')}>
          {rightBands.map(renderBand)}
        </div>
      </div>
    </div>
  );
};

const LibrarianExperience = ({
  librarianChat,
  classroom,
  session,
  initialize,
  dispose,
  sendMessage,
  resetConversation,
  openReadingSession,
  closeReadingSession,
}) => {
  const [draft, setDraft] = useState('');
  const [readerDetails, setReaderDetails] = useState(null);
  const [videoDetails, setVideoDetails] = useState(null);
  const [isLexileModalOpen, setLexileModalOpen] = useState(false);
  const classroomEnabled = classroom && classroom.data ? classroom.data.librarianEnabled : false;
  const sessionRole = session && session.data && session.data.login && session.data.login.roles
    ? session.data.login.roles[0]
    : null;
  const roleAllowsLibrarian = sessionRole === 'teacher' || sessionRole === 'student';
  const isFeatureEnabled =
    typeof window === 'undefined' ? false : Boolean(window.ENABLE_LIBRARIAN_UI) && classroomEnabled && roleAllowsLibrarian;
  const [markedReady, setMarkedReady] = useState(() => (typeof window !== 'undefined' && window.marked ? true : false));
  const [pendingRequest, setPendingRequest] = useState(false);
  const textAreaRef = useRef(null);
  const inputAreaRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const hasUserInteractedRef = useRef(false);
  const autoScrollRafRef = useRef(null);

  useEffect(() => {
    if (isFeatureEnabled) {
      initialize();
    }
    return () => {
      dispose();
    };
  }, [initialize, dispose, isFeatureEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const applyMarkedConfiguration = () => {
      if (!window.marked) {
        return false;
      }
      if (typeof window.marked.setOptions === 'function') {
        window.marked.setOptions({ breaks: true, async: false });
      }
      setMarkedReady(true);
      return true;
    };

    const handleMarkedReady = () => {
      applyMarkedConfiguration();
    };

    window.addEventListener('markedready', handleMarkedReady);

    const initialised = applyMarkedConfiguration();
    if (initialised) {
      return () => {
        window.removeEventListener('markedready', handleMarkedReady);
      };
    }

    const pollId = window.setInterval(() => {
      if (applyMarkedConfiguration()) {
        window.clearInterval(pollId);
        window.removeEventListener('markedready', handleMarkedReady);
      }
    }, 300);

    return () => {
      window.removeEventListener('markedready', handleMarkedReady);
      window.clearInterval(pollId);
    };
  }, []);

  const data = (librarianChat && librarianChat.data) || {};
  const status = (librarianChat && librarianChat.status) || 'idle';
  const isStreaming = status === 'streaming';
  const isLoading = Boolean(librarianChat && librarianChat.loading);
  const hasConversation = data.messages && data.messages.length > 0;
  useEffect(() => {
    if (!pendingRequest) {
      return;
    }
    if (!isStreaming && !isLoading) {
      setPendingRequest(false);
    }
  }, [pendingRequest, isStreaming, isLoading]);

  const isLocked = pendingRequest || isStreaming || isLoading;

  useEffect(() => {
    if (isLocked && textAreaRef.current) {
      textAreaRef.current.blur();
    }
  }, [isLocked]);

  useEffect(() => {
    if (hasAutoScrolledRef.current || hasUserInteractedRef.current || !hasConversation || isLoading) {
      return;
    }
    if (inputAreaRef.current) {
      autoScrollRafRef.current = window.requestAnimationFrame(() => {
        autoScrollRafRef.current = null;
        if (hasUserInteractedRef.current) {
          return;
        }
        inputAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
      hasAutoScrolledRef.current = true;
    }
  }, [hasConversation, isLoading]);

  useEffect(() => () => {
    if (autoScrollRafRef.current) {
      window.cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const submitPrompt = useCallback(
    async (rawMessage) => {
      const message = stringValue(rawMessage);
      if (!message || isLocked) {
        return;
      }
      hasUserInteractedRef.current = true;
      setPendingRequest(true);
      setDraft('');
      try {
        await sendMessage({ message });
      } finally {
        setPendingRequest(false);
      }
    },
    [sendMessage, isLocked],
  );

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      await submitPrompt(draft);
    },
    [draft, submitPrompt],
  );

  const handleKeyDown = (event) => {
    if (isLocked) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit(event);
    }
  };

  const handleDraftChange = (event) => {
    hasUserInteractedRef.current = true;
    if (autoScrollRafRef.current) {
      window.cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    setDraft(event.target.value);
  };

  const handleDraftFocus = () => {
    hasUserInteractedRef.current = true;
    if (autoScrollRafRef.current) {
      window.cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  };

  const handleSubmitPrompt = useCallback(
    (question) => {
      const message = stringValue(question);
      if (!message || isLocked) {
        return;
      }
      setDraft(message);
      if (typeof window === 'undefined') {
        submitPrompt(message);
        return;
      }
      window.setTimeout(() => submitPrompt(message), 0);
    },
    [submitPrompt, isLocked],
  );

  const handleOpenReader = (recommendation) => {
    if (!recommendation) {
      return;
    }
    if (isVideoRecommendation(recommendation)) {
      const videoUrl = getVideoUrl(recommendation) || firstString(recommendation.download_url);
      if (recommendation.asset_id || videoUrl) {
        setVideoDetails({
          assetId: recommendation.asset_id,
          fallbackSrc: videoUrl,
          poster: getRecommendationImage(recommendation),
          title: recommendation.title || 'Recommended video',
        });
      }
      return;
    }
    if (!recommendation.asset_id) {
      return;
    }
    const readingSessionId = generateReadingSessionId();
    const openedAt = new Date().toISOString();
    openReadingSession({
      readingSessionId,
      assetId: recommendation.asset_id,
      title: recommendation.title || null,
      openedAt,
    });
    setReaderDetails({
      assetId: recommendation.asset_id,
      title: recommendation.title || null,
      readingSessionId,
      openedAt,
    });
  };

  const handleCloseReader = () => {
    if (readerDetails && readerDetails.readingSessionId) {
      closeReadingSession({
        readingSessionId: readerDetails.readingSessionId,
        closedAt: new Date().toISOString(),
      });
    }
    setReaderDetails(null);
  };
  const handleCloseVideo = useCallback(() => setVideoDetails(null), []);
  const handleOpenLexileModal = useCallback(() => setLexileModalOpen(true), []);
  const handleCloseLexileModal = useCallback(() => setLexileModalOpen(false), []);

  if (!isFeatureEnabled) {
    return null;
  }

  const backgroundImage = require('./assets/background.png').default || require('./assets/background.png');
  const librarianWelcome = require('./assets/librarian-welcome.png').default || require('./assets/librarian-welcome.png');
  const sendIcon = require('./assets/send-icon.png').default || require('./assets/send-icon.png');
  const inputPlaceholder = hasConversation ? copy.inputPlaceholderFollowUp : copy.inputPlaceholderInitial;
  const useLegacyReader = typeof window !== 'undefined'
    && /(?:\?|&)librarianReader=(?:legacy|old|classic)(?:&|$)/.test(window.location.search);

  return (
    <section className={cx('librarian-experience')} data-marked-ready={markedReady ? 'true' : 'false'}>
      <div className={cx('hero')} style={{ backgroundImage: `url(${backgroundImage})` }}>
        {!hasConversation && !isLoading ? (
          <div className={cx('welcome')}>
            <img src={librarianWelcome} alt="AI Librarian" className={cx('character')} />
          </div>
        ) : null}

        {isLoading && !hasConversation ? (
          <div className={cx('loading')}>
            <div className={cx('spinner')} />
            <span>Loading your library session...</span>
          </div>
        ) : null}

        {hasConversation ? (
          <div className={cx('conversation-area')}>
            <MessageList
              messages={data.messages || []}
              onOpenReader={handleOpenReader}
              onOpenLexileModal={handleOpenLexileModal}
              isLexileModalOpen={isLexileModalOpen}
              onSubmitPrompt={handleSubmitPrompt}
            />
          </div>
        ) : null}

        <div className={cx('input-area')} ref={inputAreaRef}>
          <form className={cx('input-box', { locked: isLocked })} onSubmit={onSubmit}>
            {isLocked ? <div className={cx('input-veil')} /> : null}
            <input
              type="text"
              placeholder={inputPlaceholder}
              value={draft}
              onChange={handleDraftChange}
              onKeyDown={handleKeyDown}
              onFocus={handleDraftFocus}
              disabled={isLocked}
              ref={textAreaRef}
              className={cx('input-field')}
            />
            <button
              type="submit"
              className={cx('send-button', {
                'is-disabled': isLocked || !draft.trim(),
              })}
              disabled={isLocked || !draft.trim()}
              aria-label={copy.send}
            >
              <img src={sendIcon} alt="" className={cx('send-icon')} />
            </button>
          </form>

          {hasConversation ? (
            <div className={cx('secondary-actions')}>
              <div className={cx('secondary-actions__row')}>
                <button
                  type="button"
                  className={cx('button', 'button--primary', 'reset-button', { 'is-disabled': isLocked })}
                  onClick={resetConversation}
                  disabled={isLocked}
                >
                  {copy.reset}
                </button>
              </div>
              {(librarianChat && librarianChat.error) ? (
                <div className={cx('error')}>{librarianChat.error}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {readerDetails ? (
        <Modal fullSize eReader onClose={handleCloseReader}>
          {useLegacyReader
            ? <LibrarianReader assetId={readerDetails.assetId} />
            : <LibrarianReaderEmbed assetId={readerDetails.assetId} />}
        </Modal>
      ) : null}

      {videoDetails ? (
        <LibrarianVideoModal
          assetId={videoDetails.assetId}
          fallbackSrc={videoDetails.fallbackSrc}
          fullScreen={false}
          onClose={handleCloseVideo}
          poster={videoDetails.poster}
          title={videoDetails.title}
        />
      ) : null}

      {isLexileModalOpen ? (
        <Modal onClose={handleCloseLexileModal}>
          <LexileModalContent />
        </Modal>
      ) : null}
    </section>
  );
};

LibrarianExperience.propTypes = {
  librarianChat: PropTypes.object,
  classroom: PropTypes.object,
  session: PropTypes.object,
  initialize: PropTypes.func.isRequired,
  dispose: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  resetConversation: PropTypes.func.isRequired,
  openReadingSession: PropTypes.func.isRequired,
  closeReadingSession: PropTypes.func.isRequired,
};

LibrarianExperience.defaultProps = {
  librarianChat: {},
  classroom: {},
  session: {},
};

const mapStateToProps = ({ store }) => ({
  librarianChat: store.librarianChat,
  classroom: store.classroom,
  session: store.session,
});

const mapDispatchToProps = {
  initialize: actions.initialize,
  dispose: actions.dispose,
  sendMessage: actions.sendMessage,
  resetConversation: actions.resetConversation,
  openReadingSession: actions.openReadingSession,
  closeReadingSession: actions.closeReadingSession,
};

module.exports = connect(mapStateToProps, mapDispatchToProps)(LibrarianExperience);
